import { MyWorkApi } from '@/services/my-work/my-work.api'
import type { StartActionResult } from '@/services/my-work/my-work.types'

import { ClaudeFleetApi } from './claude-fleet.api'
import type { FleetJobAlerts, FleetJobAlertsChange } from './claude-fleet.job-alerts.types'
import { normalizeRunnersReply } from './claude-fleet.runners'
import { normalizeRunTargets, RUN_ON_FLEET, type FleetRunTargets } from './claude-fleet.run-on'
import { FLEET_MESSAGE_MAX_LENGTH } from './claude-fleet.utils'
import { FLEET_ANSWER_MAX_LENGTH } from './claude-fleet.watch'
import type {
  FleetAuditEntry,
  FleetBrowserControlResult,
  FleetBrowserLiveView,
  FleetCommandReply,
  FleetConfigRecord,
  FleetConfigResponse,
  FleetDeadLetterDiscardResult,
  FleetDeadLetterRetryResult,
  FleetDeadLetters,
  FleetExplainRunResult,
  FleetHealth,
  FleetInsights,
  FleetQueueName,
  FleetRunNotifyResult,
  FleetRunGradeResult,
  FleetRunGradeValue,
  FleetSettingsHistoryEntry,
  FleetStopRunResult,
  FleetRunSteps,
  FleetTranscriptLink,
  FleetLoop,
  FleetLoopctlChange,
  FleetLoopctlChangeReply,
  FleetLoopctlPauses,
  FleetLoopState,
  FleetRunDetail,
  FleetRunnerActionRequest,
  FleetRunners,
  FleetRunsPage,
  FleetRunsQuery,
  FleetSettings,
  FleetStartRunRequest,
  FleetStartRunResult,
  FleetSummary,
} from './claude-fleet.types'
import type { FleetRunnerWindow } from './claude-fleet.runner-live.types'
import type { FleetFailures, FleetLaneMove, FleetLaneQueue, FleetLaneSearchResult, FleetRunnerDetail, FleetUpcoming, FleetWaiting, FleetWaitingCancelResult, FleetWaitingStartNowResult } from './claude-fleet.queue.types'
import type { FleetJobHealth } from './claude-fleet.job-health'
import type { FleetStagingQa, FleetStagingQaAction, FleetStagingQaActionResult } from './claude-fleet.staging-qa'
import type { FleetReleaseCheckResult } from './claude-fleet.release-check'
import type { MyBuilds, Pipelines } from './claude-fleet.pipelines.types'
import type { FleetHeadsUp, FleetHeadsUpMode, FleetRequest, FleetRequestMove, FleetRequestQueue } from './claude-fleet.requests.types'
import type {
  FleetNotificationTestResult, FleetNotifications, FleetNotificationsChange, FleetPushSubscription
} from './claude-fleet.notifications.types'
import type { FleetHandBackMode, FleetRunHandBackEligibility, FleetRunHandBackResult } from './claude-fleet.hand-back'
import type { FleetRunPendingReports } from './claude-fleet.run-reports'

/**
 * Claude fleet service — the fleet's /fleet API for the Claude Fleet page.
 * No reactive state here; the store owns loading and error.
 */
export function useClaudeFleetService() {
  const listRuns = async (query: FleetRunsQuery = {}): Promise<FleetRunsPage> => {
    const page = await ClaudeFleetApi.listRuns(query)
    return { runs: page.runs ?? [], next: page.next }
  }

  const getRun = async (runId: string): Promise<FleetRunDetail> => ClaudeFleetApi.getRun(runId)

  /** The fleet rejects an empty or over-long message, so the same bounds are checked first. */
  const sayToRun = async (runId: string, text: string): Promise<FleetCommandReply> => {
    const message = text.trim()
    if (!message) throw new Error('Write a message before sending it')
    if (message.length > FLEET_MESSAGE_MAX_LENGTH) {
      throw new Error(`A message can be at most ${FLEET_MESSAGE_MAX_LENGTH} characters`)
    }
    return ClaudeFleetApi.sayToRun(runId, message)
  }

  /** The fleet refuses an empty answer or one over its limit, so the same bounds are checked first. */
  const answerRun = async (runId: string, askId: string, text: string): Promise<FleetCommandReply> => {
    const answer = text.trim()
    if (!answer) throw new Error('Pick an option or write an answer first')
    if (answer.length > FLEET_ANSWER_MAX_LENGTH) throw new Error(`An answer can be at most ${FLEET_ANSWER_MAX_LENGTH} characters`)
    return ClaudeFleetApi.answerRun(runId, askId, answer)
  }

  const pauseRun = async (runId: string): Promise<FleetCommandReply> => ClaudeFleetApi.pauseRun(runId)

  const resumeRun = async (runId: string, text?: string): Promise<FleetCommandReply> =>
    ClaudeFleetApi.resumeRun(runId, text?.trim() || undefined)

  const stopRun = async (runId: string): Promise<FleetStopRunResult> => ClaudeFleetApi.stopRun(runId)

  const explainRun = async (runId: string): Promise<FleetExplainRunResult> => ClaudeFleetApi.explainRun(runId)

  const setRunNotify = async (runId: string, on: boolean): Promise<FleetRunNotifyResult> =>
    ClaudeFleetApi.setRunNotify(runId, on)

  const gradeRun = async (runId: string, grade: FleetRunGradeValue | null, why?: string): Promise<FleetRunGradeResult> =>
    ClaudeFleetApi.gradeRun(runId, grade, why)

  const getBrowserLiveView = async (runId: string): Promise<FleetBrowserLiveView> =>
    ClaudeFleetApi.getBrowserLiveView(runId)

  const setBrowserControl = async (runId: string, take: boolean): Promise<FleetBrowserControlResult> =>
    ClaudeFleetApi.setBrowserControl(runId, take)

  const getRunSteps = async (runId: string): Promise<FleetRunSteps> => {
    const steps = await ClaudeFleetApi.getRunSteps(runId)
    return { ...steps, groups: steps.groups ?? [] }
  }

  const getTranscriptLink = async (runId: string): Promise<FleetTranscriptLink> => ClaudeFleetApi.getTranscriptLink(runId)

  const listDeadLetters = async (queue: FleetQueueName): Promise<FleetDeadLetters> => {
    const deadLetters = await ClaudeFleetApi.listDeadLetters(queue)
    return { ...deadLetters, messages: deadLetters.messages ?? [], more: Boolean(deadLetters.more) }
  }

  const retryDeadLetters = async (queue: FleetQueueName, messageId?: string): Promise<FleetDeadLetterRetryResult> =>
    ClaudeFleetApi.retryDeadLetters(queue, messageId)

  const discardDeadLetters = async (queue: FleetQueueName, messageIds: string[]): Promise<FleetDeadLetterDiscardResult> =>
    ClaudeFleetApi.discardDeadLetters(queue, messageIds)

  /** `notify` is sent only when asked for, so a start without it reads the same to an older fleet service. */
  const startRun = async (request: FleetStartRunRequest): Promise<FleetStartRunResult> =>
    ClaudeFleetApi.startRun({
      activity: request.activity.trim(),
      command: request.command.trim(),
      ...(request.notify ? { notify: true } : {}),
      ...(request.runOn && request.runOn !== RUN_ON_FLEET ? { runOn: request.runOn } : {})
    })

  const listLoops = async (): Promise<FleetLoop[]> => ClaudeFleetApi.listLoops()

  const setLoopState = async (name: string, state: FleetLoopState): Promise<FleetCommandReply> =>
    ClaudeFleetApi.setLoopState(name, state)

  const listLoopctlPauses = async (): Promise<FleetLoopctlPauses> => ClaudeFleetApi.listLoopctlPauses()

  const setLoopctl = async (change: FleetLoopctlChange): Promise<FleetLoopctlChangeReply> => ClaudeFleetApi.setLoopctl(change)

  const getSummary = async (): Promise<FleetSummary> => ClaudeFleetApi.getSummary()

  const listAudit = async (): Promise<FleetAuditEntry[]> => ClaudeFleetApi.listAudit()

  const getHealth = async (): Promise<FleetHealth> => ClaudeFleetApi.getHealth()
  const getJobHealth = async (): Promise<FleetJobHealth> => ClaudeFleetApi.getJobHealth()
  const getInsights = async (): Promise<FleetInsights> => ClaudeFleetApi.getInsights()
  const getReleaseStagingQa = async (version?: string): Promise<FleetStagingQa> => ClaudeFleetApi.getReleaseStagingQa(version)
  const actOnReleaseStagingQa = async (action: FleetStagingQaAction, version: string): Promise<FleetStagingQaActionResult> =>
    ClaudeFleetApi.actOnReleaseStagingQa(action, version)
  const checkRelease = async (): Promise<FleetReleaseCheckResult> => ClaudeFleetApi.checkRelease()

  const getPipelines = async (): Promise<Pipelines> => ClaudeFleetApi.getPipelines()
  /** A fleet that sent no list reads as no branch the viewer may have broken. */
  const getMyBuilds = async (): Promise<MyBuilds> => ({ branches: (await ClaudeFleetApi.getMyBuilds())?.branches ?? [] })

  const getConfigHistory = async (): Promise<FleetSettingsHistoryEntry[]> =>
    (await ClaudeFleetApi.getConfigHistory()) ?? []

  const listRunners = async (): Promise<FleetRunners> =>
    normalizeRunnersReply(await ClaudeFleetApi.listRunners())

  const getRunTargets = async (): Promise<FleetRunTargets> =>
    normalizeRunTargets(await ClaudeFleetApi.getRunTargets())

  const getConfig = async (): Promise<FleetConfigResponse> => {
    const response = await ClaudeFleetApi.getConfig()
    return {
      config: response.config ?? {},
      ceilings: response.ceilings ?? {},
      ...(typeof response.canEdit === 'boolean' ? { canEdit: response.canEdit } : {}),
      describe: response.describe ?? {},
      kinds: response.kinds ?? {},
      deployOverrides: response.deployOverrides ?? []
    }
  }

  const getRunner = async (machine: string): Promise<FleetRunnerDetail> => {
    const detail = await ClaudeFleetApi.getRunner(machine)
    return { ...detail, events: detail.events ?? [], workingOn: detail.workingOn ?? [], recent: detail.recent ?? [] }
  }

  const getRunnerWindow = async (machine: string): Promise<FleetRunnerWindow> => {
    const window = await ClaudeFleetApi.getRunnerWindow(machine)
    return { ...window, machine: window.machine ?? machine, groups: window.groups ?? [] }
  }

  const getFailures = async (days: number): Promise<FleetFailures> => {
    const failures = await ClaudeFleetApi.getFailures(days)
    return { ...failures, groups: failures.groups ?? [] }
  }

  const listWaiting = async (): Promise<FleetWaiting> => ({ waiting: (await ClaudeFleetApi.listWaiting()).waiting ?? [] })
  const cancelWaiting = async (keys: string[], byName?: string): Promise<FleetWaitingCancelResult> => ClaudeFleetApi.cancelWaiting(keys, byName)
  /** A PR fix or review, as a Fix findings or Review click on My work queues it: an operator may on any PR. */
  const startPrNow = async (action: string, target: string): Promise<StartActionResult> => MyWorkApi.startAction({ action, target })
  const startWaitingNow = async (keys: string[], byName?: string): Promise<FleetWaitingStartNowResult> => ClaudeFleetApi.startWaitingNow(keys, byName)

  const getUpcoming = async (): Promise<FleetUpcoming> => {
    const upcoming = await ClaudeFleetApi.getUpcoming()
    return {
      ...upcoming,
      scheduled: upcoming.scheduled ?? [],
      pending: upcoming.pending ?? [],
      ownerOnly: upcoming.ownerOnly ?? [],
      queues: upcoming.queues ?? {},
      counts: upcoming.counts ?? { byActivity: {}, byDestination: {}, taken: 0 }
    }
  }
  const getLaneQueue = async (): Promise<FleetLaneQueue> => ClaudeFleetApi.getLaneQueue()

  const moveLaneQueueTicket = async (move: { key: string; position?: number; from?: number; reset?: boolean }): Promise<FleetLaneMove[]> =>
    (await ClaudeFleetApi.moveLaneQueueTicket(move)).moves

  const searchLaneQueue = async (q: string): Promise<FleetLaneSearchResult[]> => (await ClaudeFleetApi.searchLaneQueue(q)).results ?? []

  const addLaneQueueTicket = async (add: { key: string; position?: number }): Promise<FleetLaneMove[]> =>
    (await ClaudeFleetApi.addLaneQueueTicket(add)).moves

  const getRequest = async (id: string): Promise<FleetRequest> => (await ClaudeFleetApi.getRequest(id)).request

  const getRequestQueue = async (): Promise<FleetRequestQueue> => {
    const queue = await ClaudeFleetApi.getRequestQueue()
    return { ...queue, items: queue.items ?? [], counts: queue.counts ?? { queued: 0, running: 0 } }
  }

  const cancelRequest = async (id: string, reason?: string): Promise<FleetRequest> =>
    (await ClaudeFleetApi.cancelRequest(id, reason?.trim() || undefined)).request

  const moveRequest = async (id: string, to: FleetRequestMove): Promise<FleetRequest> => (await ClaudeFleetApi.moveRequest(id, to)).request

  const getRunHandBack = async (runId: string): Promise<FleetRunHandBackEligibility> => ClaudeFleetApi.getRunHandBack(runId)

  const getRunPendingReports = async (runId: string): Promise<FleetRunPendingReports> => ClaudeFleetApi.getRunPendingReports(runId)

  const closeRunPendingReports = async (runId: string): Promise<FleetRunPendingReports> => ClaudeFleetApi.closeRunPendingReports(runId)

  const handBackRun = async (runId: string, mode: FleetHandBackMode): Promise<FleetRunHandBackResult> =>
    (await ClaudeFleetApi.handBackRun(runId, mode === 'mine')).handBack

  const getNotifications = async (): Promise<FleetNotifications> => ClaudeFleetApi.getNotifications()

  const getJobAlerts = async (): Promise<FleetJobAlerts> => ClaudeFleetApi.getJobAlerts()

  const saveJobAlerts = async (change: FleetJobAlertsChange): Promise<FleetJobAlerts> => ClaudeFleetApi.saveJobAlerts(change)

  const saveNotifications = async (change: FleetNotificationsChange): Promise<FleetNotifications> =>
    ClaudeFleetApi.saveNotifications(change)

  const addNotificationBrowser = async (subscription: FleetPushSubscription, device: string): Promise<FleetNotifications> =>
    ClaudeFleetApi.addNotificationBrowser(subscription, device)

  const removeNotificationBrowser = async (id: string): Promise<FleetNotifications> => ClaudeFleetApi.removeNotificationBrowser(id)

  const sendTestNotification = async (): Promise<FleetNotificationTestResult> => ClaudeFleetApi.sendTestNotification()

  const getHeadsUp = async (): Promise<FleetHeadsUp> => ClaudeFleetApi.getHeadsUp()

  const setHeadsUp = async (mode: FleetHeadsUpMode): Promise<FleetHeadsUp> => ClaudeFleetApi.setHeadsUp(mode)

  const sendRunnerAction = async (machine: string, request: FleetRunnerActionRequest): Promise<FleetCommandReply> =>
    ClaudeFleetApi.sendRunnerAction(machine, request)

  /** Sends only the settings that changed; the fleet audits each one, so an empty save is refused here. */
  const updateConfig = async (activity: string, settings: FleetSettings): Promise<FleetConfigRecord> => {
    if (Object.keys(settings).length === 0) throw new Error('There are no changes to save')
    return ClaudeFleetApi.updateConfig(activity, settings)
  }

  return {
    listRuns,
    getRun,
    sayToRun,
    answerRun,
    pauseRun,
    resumeRun,
    stopRun,
    explainRun,
    setRunNotify,
    gradeRun,
    getBrowserLiveView,
    setBrowserControl,
    getRunSteps,
    getTranscriptLink,
    listDeadLetters,
    retryDeadLetters,
    discardDeadLetters,
    startRun,
    listLoops,
    setLoopState,
    listLoopctlPauses,
    setLoopctl,
    getSummary,
    listAudit,
    getHealth,
    getJobHealth,
    getInsights,
    getReleaseStagingQa,
    actOnReleaseStagingQa,
    checkRelease,
    getPipelines,
    getMyBuilds,
    getConfigHistory,
    listRunners,
    getRunTargets,
    getRunner,
    getRunnerWindow,
    getFailures,
    listWaiting,
    cancelWaiting,
    startWaitingNow,
    startPrNow,
    getUpcoming,
    getLaneQueue,
    moveLaneQueueTicket,
    searchLaneQueue,
    addLaneQueueTicket,
    getRequest,
    getRequestQueue,
    cancelRequest,
    moveRequest,
    getRunHandBack,
    handBackRun,
    getRunPendingReports,
    closeRunPendingReports,
    getNotifications,
    saveNotifications,
    getJobAlerts,
    saveJobAlerts,
    addNotificationBrowser,
    removeNotificationBrowser,
    sendTestNotification,
    getHeadsUp,
    setHeadsUp,
    sendRunnerAction,
    getConfig,
    updateConfig
  }
}
